import { useState, useEffect } from 'react';
import SEO, { PAGE_SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Brain, Sparkles, Clock, Lightbulb } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get SEO-friendly content URL
const getContentUrl = (item) => `/content/${item.slug || item.content_id}`;

export default function KnowledgeCorner() {
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/content?content_type=knowledge&limit=20`)
      .then(res => res.json())
      .then(data => setKnowledge(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="knowledge-corner-page">
      <SEO {...PAGE_SEO.knowledge} url="/knowledge" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <Brain className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Education</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-4">Knowledge Corner</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Understand the science behind skincare and haircare with educational content from experts.
            </p>
          </div>

          {/* Knowledge Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="border-none">
                  <CardContent className="p-8">
                    <Skeleton className="h-12 w-12 rounded-sm mb-6" />
                    <Skeleton className="h-4 w-24 mb-4" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : knowledge.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No knowledge articles available yet.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {knowledge.map((item, index) => (
                <Link 
                  key={item.content_id} 
                  to={getContentUrl(item)}
                  className={`group ${index === 0 ? 'md:col-span-2 lg:col-span-2' : ''}`}
                  data-testid={`knowledge-item-${item.content_id}`}
                >
                  <Card className="bg-card border-none shadow-card card-hover h-full">
                    <CardContent className={`${index === 0 ? 'p-10' : 'p-8'}`}>
                      <div className="w-12 h-12 rounded-sm bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                        <Lightbulb className="w-6 h-6 text-gold" />
                      </div>
                      <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
                        <span className="text-gold font-semibold uppercase tracking-wider">{item.category}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {item.read_time} min
                        </span>
                      </div>
                      <h2 className={`font-serif font-semibold mb-3 group-hover:text-gold transition-colors ${index === 0 ? 'text-2xl' : 'text-lg'}`}>
                        {item.title}
                      </h2>
                      <p className={`text-muted-foreground ${index === 0 ? 'line-clamp-3' : 'line-clamp-2'}`}>
                        {item.summary}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-6">
                        {item.tags?.slice(0, 3).map((tag, i) => (
                          <span key={i} className="px-2 py-1 bg-secondary text-xs rounded-sm">{tag}</span>
                        ))}
                        <span className="flex items-center gap-1 px-2 py-1 bg-gold/10 rounded-sm ml-auto">
                          <Sparkles className="w-3 h-3 text-gold" />
                          <span className="text-xs font-semibold text-gold">+{item.points_reward} pts</span>
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
